import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing, borderRadius, fontSize, fontWeight, shadows } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';

// ─── Styled Components (van StatsScreen) ─────────────────────────────────────────
const Container = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  padding: ${spacing.xl};
  height: calc(100vh - 52px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const HeaderLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;


const Title = styled.h1`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const DateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const DatePopover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  margin-top: ${spacing.xs};
  box-shadow: ${shadows.lg};
  min-width: 280px;
`;

const Section = styled.div`
  margin-bottom: ${spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const SectionCount = styled.span`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.muted};
`;

const ProductCatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.lg};
`;

const ProductCat = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  box-shadow: ${shadows.sm};
`;

const ProductCatTitle = styled.h3`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing.md} 0;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.xs} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ProductRank = styled.span`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.muted};
  min-width: 20px;
`;

const ProductImg = styled.img`
  width: 24px;
  height: 24px;
  border-radius: ${borderRadius.sm};
  object-fit: cover;
`;

const ProductPlaceholder = styled.div`
  width: 24px;
  height: 24px;
  border-radius: ${borderRadius.sm};
  background: ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${props => props.theme.colors.muted};
`;

const ProductName = styled.div`
  flex: 1;
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProductQty = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.muted};
`;

const ProductRev = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
`;

const ExpandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} 0;
  font-size: ${fontSize.xs};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: ${spacing.xs};

  &:hover {
    background: ${props => props.theme.colors.background};
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.muted};
  font-size: ${fontSize.md};
  padding: ${spacing.xl};
`;

const EmptyText = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.muted};
  font-size: ${fontSize.md};
  padding: ${spacing.xl};
`;

// ─── Component ───────────────────────────────────────────────────────────────
export default function ProductAnalyticsScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topItemsByCategory, setTopItemsByCategory] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');

  const formatDateKey = (date) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  const getDateLabel = () => {
    if (!selectedStartDate && !selectedEndDate) return 'Alle data';
    if (selectedStartDate === selectedEndDate) {
      return new Date(selectedStartDate).toLocaleDateString('nl-NL', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    }
    const start = selectedStartDate ? new Date(selectedStartDate).toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'short' 
    }) : '...';
    const end = selectedEndDate ? new Date(selectedEndDate).toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    }) : '...';
    return `${start} - ${end}`;
  };

  const loadTopProducts = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Starting loadTopProducts...');
      
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) {
        console.log('❌ No kmfcClient');
        setLoading(false);
        return;
      }

      // Use exact same method as StatsScreen
      console.log('📊 Fetching orders...');
      const todayStr = formatDateKey(new Date());
      const safeStart = selectedStartDate || todayStr;
      const safeEnd = selectedEndDate || todayStr;

      const { data: allOrders, error } = await kmfcClient
        .from('orders')
        .select('id, placed_at, status, is_test')
        .gte('placed_at', safeStart)
        .lte('placed_at', safeEnd + 'T23:59:59')
        .order('placed_at', { ascending: false });
      if (error) throw error;

      console.log(`📦 Found ${allOrders?.length || 0} total orders`);

      if (!allOrders?.length) {
        console.log('❌ No orders found');
        setTopItemsByCategory({});
        setLoading(false);
        return;
      }

      const PAID_STATUSES = ['nieuw', 'bereiding', 'klaar', 'onderweg', 'bezorgd', 'voltooid'];
      const rangeOrders = allOrders.filter(o => !o.is_test && PAID_STATUSES.includes(o.status) && o.placed_at);
      
      console.log(`✅ Filtered to ${rangeOrders.length} orders in date range`);

      // Get order items like StatsScreen does
      const orderIds = rangeOrders.map(o => o.id);
      let orderItems = [];
      if (orderIds.length > 0) {
        console.log('📊 Fetching order items...');
        const { data: items } = await kmfcClient.from('order_items').select('order_id, name_snapshot, qty, line_total_cents').in('order_id', orderIds);
        if (items) orderItems = items;
      }

      // Get products data like StatsScreen does
      console.log('📊 Fetching products...');
      const { data: productsData } = await kmfcClient.from('products').select('name, image_url, categories!inner(name)').eq('is_active', true);
      const products = productsData || [];
      const productMap = {};
      products.forEach(p => { 
        productMap[p.name] = { imageUrl: p.image_url, category: p.categories?.name || 'Overig' }; 
      });

      console.log(`📦 Found ${products.length} products, ${orderItems.length} order items`);

      // Process data like StatsScreen does
      const allProdData = {};
      products.forEach(p => {
        const cat = p.categories?.name || 'Overig';
        if (!allProdData[cat]) allProdData[cat] = {};
        if (!allProdData[cat][p.name]) allProdData[cat][p.name] = { name: p.name, quantity: 0, totalRevenue: 0, imageUrl: p.image_url };
      });

      orderItems.forEach(item => {
        const name = item.name_snapshot || 'Onbekend';
        const info = productMap[name], cat = info?.category || 'Overig';
        if (!allProdData[cat]) allProdData[cat] = {};
        if (!allProdData[cat][name]) allProdData[cat][name] = { name, quantity: 0, totalRevenue: 0, imageUrl: info?.imageUrl || null };
        allProdData[cat][name].quantity += item.qty || 0;
        allProdData[cat][name].totalRevenue += (item.line_total_cents || 0) / 100;
      });

      // Sort like StatsScreen does
      const finalData = {};
      Object.entries(allProdData).forEach(([cat, items]) => { 
        finalData[cat] = Object.values(items).sort((a, b) => b.quantity - a.quantity); 
      });

      console.log(`✅ Final data ready: ${Object.keys(finalData).length} categories`);
      Object.keys(finalData).forEach(cat => {
        console.log(`📦 ${cat}: ${finalData[cat].length} products`);
      });

      setTopItemsByCategory(finalData);
    } catch (error) {
      console.error('❌ Error loading top products:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    loadTopProducts();
  }, [loadTopProducts]);

  return (
    <Container>
      <Content>
        <HeaderLayout>
          <HeaderLeft>
            <Title>Producten</Title>
          </HeaderLeft>
          <HeaderRight>
            <div style={{ position: 'relative' }}>
              <DateBadge
                active={isDatePickerOpen}
                onClick={() => setIsDatePickerOpen(p => !p)}
              >
                <Calendar size={10} />
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11 }}>
                  {getDateLabel()}
                </span>
                <ChevronDown size={8} />
              </DateBadge>
              {isDatePickerOpen && (
                <DatePopover>
                  <div style={{ marginBottom: spacing.md }}>
                    <label style={{ display: 'block', marginBottom: spacing.sm, fontSize: fontSize.sm, fontWeight: 'medium' }}>
                      Startdatum
                    </label>
                    <input
                      type="date"
                      value={selectedStartDate}
                      onChange={(e) => setSelectedStartDate(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: `1px solid ${theme.colors.border}`, borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: spacing.md }}>
                    <label style={{ display: 'block', marginBottom: spacing.sm, fontSize: fontSize.sm, fontWeight: 'medium' }}>
                      Einddatum
                    </label>
                    <input
                      type="date"
                      value={selectedEndDate}
                      onChange={(e) => setSelectedEndDate(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: `1px solid ${theme.colors.border}`, borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: spacing.md }}>
                    <button
                      onClick={() => {
                        setSelectedStartDate('');
                        setSelectedEndDate('');
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: 'transparent',
                        color: theme.colors.text,
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Wis filters
                    </button>
                  </div>
                  <button
                    onClick={() => setIsDatePickerOpen(false)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: theme.colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Toepassen
                  </button>
                </DatePopover>
              )}
            </div>
          </HeaderRight>
        </HeaderLayout>

        {loading ? (
          <LoadingText>Laden...</LoadingText>
        ) : Object.keys(topItemsByCategory).length === 0 ? (
          <EmptyText>Geen productdata beschikbaar</EmptyText>
        ) : (
          <Section>
            <SectionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <SectionTitle>Meest verkocht per categorie</SectionTitle>
              </div>
              <SectionCount>{Object.keys(topItemsByCategory).length} categorieën</SectionCount>
            </SectionHeader>
            <ProductCatGrid>
              {Object.entries(topItemsByCategory).map(([cat, items]) => {
                const expanded = expandedCategories[cat] || false;
                const shown = expanded ? items : items.slice(0, 5);
                return (
                  <ProductCat key={cat}>
                    <ProductCatTitle>{cat}</ProductCatTitle>
                    {items.length === 0
                      ? <div style={{ fontSize: 11, color: '#333', padding: '6px 0' }}>Geen items</div>
                      : <>
                        {shown.map((item, i) => (
                          <ProductRow key={i}>
                            <ProductRank>{i + 1}</ProductRank>
                            {item.imageUrl
                              ? <ProductImg src={item.imageUrl} alt={item.name} onError={e => { e.target.style.display = 'none'; }} />
                              : <ProductPlaceholder>-</ProductPlaceholder>}
                            <ProductName>{item.name}</ProductName>
                            <ProductQty>{item.quantity}×</ProductQty>
                            <ProductRev>€{item.totalRevenue.toFixed(2)}</ProductRev>
                          </ProductRow>
                        ))}
                        {items.length > 5 && (
                          <ExpandRow onClick={() => setExpandedCategories(p => ({ ...p, [cat]: !p[cat] }))}>
                            {expanded ? <><ChevronUp size={10} /> toon minder</> : <><ChevronDown size={10} /> {items.length - 5} meer</>}
                          </ExpandRow>
                        )}
                      </>}
                  </ProductCat>
                );
              })}
            </ProductCatGrid>
          </Section>
        )}
      </Content>
    </Container>
  );
}
